import { Buffer } from 'buffer'
import { type IsEmpty, isEmpty, type IsNullable } from './is_empty'
import { isObjectID, ObjectIDtoString, ObjectIDtoStringStrict } from './object_id_helper'
import type { AnyObject, Collection, DeepPartial, Many, ObjectType } from './types/custom_type.types'

export const MAX_RECURSION_DEPTH_HIGH = Number(process.env.MAX_RECURSION_DEPTH_HIGH ?? 32)
export const MAX_RECURSION_DEPTH_MEDIUM = Number(process.env.MAX_RECURSION_DEPTH_MEDIUM ?? 20)
export const MAX_RECURSION_DEPTH_LOW = Number(process.env.MAX_RECURSION_DEPTH_LOW ?? 8)

/* ------------------------------- NO CATEGORY ------------------------------ */
export const sleep = async (ms = 1000) => new Promise(resolve => {
  setTimeout(resolve, ms)
})
/**
 * MaybeSameAsString. comparing 2 variables that somehow has the same result when converted to String. Excluding literal Object, null, undefined
 * @param valA
 * @param valB
 * @returns
 */
export const maybeEqualAsString = (valueA: unknown, valueB: unknown) => {
  try {
    return !isEmpty(valueA) && !isEmpty(valueB)
    && String(valueA) === String(valueB)
    && ![`object`, `null`, `undefined`].includes(String(valueA))
    && ![`object`, `null`, `undefined`].includes(String(valueB))
  } catch (error) {
    console.warn({ error })
    return false
  }
}
/* --------------------------- END OF NO CATEGORY --------------------------- */

/* ---------------------------- ARRAY LIKE UTILS ---------------------------- */

export const findAndReplace = <T, R>(
  array: Array<T | R>,
  filter: DeepPartial<T | R>,
  replace: (
    matched: T | R | undefined,
    index: number,
    array_: Array<T | R>,
    filterFullfil: boolean
  ) => R,
): T | R | undefined => {
  const immutable: Array<T | R> = Object.assign([], array)
  find(immutable, filter, (a, index, c, pass) => {
    if (pass && typeof replace === `function`)
      immutable.splice(index, 1, replace(a, index, c, pass))

    return pass
  })
  return find(immutable, filter)
}

export const findManyAndReplace = <T, R>(
  array: Array<T | R>,
  filter: DeepPartial<T | R>,
  replace: (
    matched: T | R | undefined,
    index: number,
    array_: Array<T | R>,
    filterFullfil: boolean
  ) => R,
): Collection<T | R | undefined> => {
  const immutable: Array<T | R> = Object.assign([], array)
  findMany(immutable, filter, (a, index, c, pass) => {
    if (pass && typeof replace === `function`)
      immutable.splice(index, 1, replace(a, index, c, pass))

    return pass
  })
  return findMany(immutable, filter)
}

export const compareObjectDeep = (criteria: Record<any, any>, value: object, depth_ = MAX_RECURSION_DEPTH_LOW): boolean => {
  const recursive = (criteria: Record<any, any>, value: object, _depth): boolean => criteria === value || (criteria && Object.keys(criteria).every(criteriaKey => {
    if (!(criteriaKey in value)) return false
    const criteriaValue = criteria[criteriaKey]
    const actualValue = value[criteriaKey]

    if (typeof criteriaValue !== typeof actualValue) return false
    if (typeof criteriaValue === `object` && _depth > 0)
      return recursive(criteriaValue, actualValue, _depth - 1)

    return criteriaValue === actualValue
        || maybeEqualAsString(criteriaValue, actualValue)
        || (criteriaValue instanceof RegExp && criteriaValue.test(actualValue))
  }))
  return recursive(criteria, value, depth_)
}

const _deepFindFullfilFunction = <T>(filter: any, element: any): boolean => filter && Object.keys(filter).every((filterKey: string) => {
  if (isEmpty(filter)) return false
  const elementValue = element[filterKey as keyof T]
  const filterValue = filter[filterKey as keyof T]

  if (typeof filterValue === `object` && !isEmpty(filterValue)) {
    if (typeof elementValue !== `object`)
      return false

    return compareObjectDeep(Object.assign(filterValue ?? {}, {}), elementValue)
  }

  return (element[filterKey as keyof T] === filter[filterKey as keyof T])
      || maybeEqualAsString(element[filterKey as keyof T], filter[filterKey as keyof T])
})

export const find = <T>(col: Collection<T> | undefined, filter?: DeepPartial<T>, customCondition?: (element: T, index: number, _: T[], filterFullfil: boolean) => boolean): T | undefined => {
  if ((isEmpty(filter) && !customCondition) || isEmpty(col))
    return void 0

  return Array.isArray(col)
    ? col.find(
      (element, index) => {
        const fullfil = _deepFindFullfilFunction<T>(filter, element)
        const isFulfilled = (!isEmpty(filter) && fullfil)

        return typeof customCondition === `function` && !isEmpty(filter)
          ? ((isFulfilled) && customCondition(element, index, col, isFulfilled))
          : (typeof customCondition === `function`
            ? customCondition(element, index, col, isFulfilled)
            : (isFulfilled))
      },
    )
    : void 0
}

export const findMany = <T>(col: Collection<T> | undefined, filter?: DeepPartial<T>, customCondition?: (element: T, index: number, _: T[], filterFullfil: boolean) => boolean): T[] => {
  if ((isEmpty(filter) && !customCondition) || isEmpty(col))
    return []

  return Array.isArray(col)
    ? col.filter(
      (element, index) => {
        const fullfil = _deepFindFullfilFunction<T>(filter, element)
        const isFulfilled = (!isEmpty(filter) && fullfil)

        return typeof customCondition === `function` && !isEmpty(filter)
          ? ((isFulfilled) && customCondition(element, index, col, isFulfilled))
          : (typeof customCondition === `function`
            ? customCondition(element, index, col, isFulfilled)
            : (isFulfilled))
      },
    )
    : []
}

export const chunk = <T>(input: Collection<T>, size: number) =>
  Array.isArray(input)
  // ? input.reduce<T[][]>((accumulator, element, index) => {
  //   const batchIndex = Math.floor(index / size)
  //   if (Array.isArray(accumulator[batchIndex]))
  //     accumulator[batchIndex].push(element)
  //   else
  //     accumulator.push([element])

  //     return accumulator
  //   }, [])
    ? input.reduce<T[][]>((array_, item, index) => index % size === 0
      ? [...array_, [item]]
      : [...array_.slice(0, -1), [...array_.slice(-1)[0], item]], [])
    : []

export const batchOf = (index: number, total: number, batchSize: number) => `${ index + 1 } of ${ Math.floor(total / batchSize) + 1 } (${ total })`

/**
 * Transform. transforming object like with customable callback that change the result realtime.
 * @param obj
 * @param callback
 * @returns
 */
export const transform = <R extends ObjectType, T>(
  cols: ObjectType<T> | Collection<T>,
  callback: (
    result: R,
    value: T,
    key: string | number
  ) => void,
): R | undefined => {
  if (isObject(cols)) {
    const result = Object.keys(cols)
      .reduce(
        (result, colKey) => {
          callback(result, cols[colKey], colKey)
          return result
        }, Object.assign(Array.isArray(cols) ? [] : {}),
      )
    return result
  }

  return void 0
}

/**
 * Sorting array by multiple key
 *
 * @param xs collections must be array
 * @param keys filter by this keys
 * @param argOrders if order is not specified, it will be default to ascending, if its only single value, all the key will be ordered by that value
 * @returns
 */

export const orderBy = <
  T extends Record<string, any>,
  Ts extends Collection<T> = never,
>(
    xs: Ts | Collection<T> | undefined,
    sortingRule: Partial<Record<keyof T, (`asc` | `desc` | -1 | 1) | Array<`asc` | `desc` | -1 | 1>>>,
  ): Ts => {
  if (!Array.isArray(xs))
    return (xs ?? []) as Ts

  if (xs.length <= 1)
    return ((xs as any) ?? []) as Ts

  const keys = Object.keys(sortingRule)

  if (!Array.isArray(keys))
    throw new TypeError(`keys must be array`)

  const sorted = xs.sort((a, b) => {
    let sortValue = 0
    for (const [index, key] of keys.entries()) {
      const aValue = a[key]
      const bValue = b[key]
      if (aValue > bValue)
        sortValue = (sortingRule[key] === -1 || sortingRule[key] === 1 ? sortingRule[key] as number : void 0) ?? (sortingRule[key] === `asc` ? 1 : -1)

      if (aValue < bValue)
        sortValue = (sortingRule[key] === -1 || sortingRule[key] === 1 ? (sortingRule[key] as number) * -1 : void 0) ?? (sortingRule[key] === `asc` ? -1 : 1)
    }

    return sortValue
  })
  return (sorted as any) as Ts
}

export const orderByKeys = <
  T extends Record<string, any>,
  Ts extends Collection<T> = never,
  U extends keyof T = never,
>(
    xs: Ts | Collection<T> | undefined,
    keys: Array<Many<U>> = [],
    sortingRule?: (`asc` | `desc` | -1 | 1) | Array<`asc` | `desc` | -1 | 1>,
  ): Ts => {
  if (!Array.isArray(xs))
    return (xs ?? []) as Ts

  if (xs.length <= 1)
    return ((xs as any) ?? []) as Ts

  if (!Array.isArray(keys))
    throw new TypeError(`keys must be array`)

  let sortingRules: Array<string | number> = []

  if (typeof sortingRule === `string` && [`asc`, `desc`].includes(sortingRule)) {
    sortingRules = new Array(keys.length).fill(sortingRule)
  } else if (Array.isArray(sortingRule) && keys.length !== sortingRule.length) {
    sortingRules = keys.map((key, index) =>
      [`asc`, `desc`].includes(String(sortingRule[index])) ? sortingRule[index] : `asc`,
    )
  } else if (Array.isArray(sortingRule) && keys.length === sortingRule.length) {
    sortingRules = sortingRule
  } else {
    sortingRules = new Array(keys.length).fill(`asc`)
  }

  const sorted = xs.sort((a, b) => {
    let sortValue = 0
    for (const [index, key] of keys.entries()) {
      const aValue = a[key]
      const bValue = b[key]
      if (aValue > bValue)
        sortValue = (sortingRules[index] === -1 || sortingRules[index] === 1 ? sortingRules[index] as number : void 0) ?? (sortingRules[index] === `asc` ? 1 : -1)

      if (aValue < bValue)
        sortValue = (sortingRules[index] === -1 || sortingRules[index] === 1 ? (sortingRules[index] as number) * -1 : void 0) ?? (sortingRules[index] === `asc` ? -1 : 1)
    }

    return sortValue
  })
  return (sorted as any) as Ts
}

export const groupBy = <T extends Record<string, any>, C extends Collection<T>>(xs: C, key: string) => {
  const grouped = Array.isArray(xs) ? xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] ?? []).push(x)
    return rv
  }, {}) : []

  return grouped as Record<string, C[]>
}

export const mergeArrayByKeys = <T extends Record<string, any>, Ts extends Collection<T>>(xs: Ts) => {
  let mostBigObject: Record<string, unknown> = {}
  mostBigObject = Array.isArray(xs)
    ? xs.reduce(
      (most, current) => {
        if (typeof current === `object` && Object.keys(current) > Object.keys(most))
          most = current

        return most
      },
      {} as unknown,
    )
    : void 0

  const mapped: Record<string, T[]> = {}
  Object.keys(mostBigObject).map(
    mapKey => {
      Array.isArray(xs)
        ? xs.map(
          x => {
            Array.isArray(mapped[mapKey])
              ? mapped[mapKey].push(x[mapKey])
              : mapped[mapKey] = [x[mapKey]]
          },
        )
        : void 0
    })
  return mapped
}

export const uniqueBy = <T>(array: T[], key: keyof T) => {
  // Method 1 - potential memory leak, because adding value to the set
  // but this method still better than using native array
  const seen = new Set()
  return array.filter(element => {
    const keyValue = element[key]
    if (seen.has(keyValue))
      return false

    seen.add(keyValue)
    return true
  })

  // Method 2 - no memory leak, but slower
  // return arr.filter(
  //   (el, index, self) => self.findIndex(
  //       ({ [key]: keyValue }) => keyValue === el[key],
  //     ) === index,
  // )
}

/**
 * ArrayFilter. filter nullable values from array
 * @param array
 * @returns
 */
export const arrayFilter = <T>(array: Collection<T>) => {
  let filtered: Array<NonNullable<T>> = []
  if (Array.isArray(array) && array.length > 0)
    filtered = array.filter(arrayFilterFunc)

  return filtered
}

/**
 * ArrayFilterFunc. only the function for filtering nullable from array
 * @param val
 * @returns
 */

export const arrayFilterFunc = <T>(value: T): value is Exclude<T, IsNullable> => value !== null && value !== undefined

export const arrayFilterEmptyFunc = <T>(value: T): value is Exclude<T, IsEmpty | IsNullable> => !isEmpty(value)

export const maxNumber = (array: number[]) => array.length > 0 ? Math.max(...array.map(item => toNumberOrUndefined(item) ?? 0)) : 0
export const minNumber = (array: number[]) => array.length > 0 ? Math.min(...array.map(item => toNumberOrUndefined(item) ?? 0)) : 0
export const sumNumbers = (array: number[]) => array.length > 0 ? array.reduce((accumulator, element) => (toNumberOrUndefined(accumulator) ?? 0) + (toNumberOrUndefined(element) ?? 0), 0) : 0
export const avgNumbers = (array: number[]) => array.length > 0 ? sumNumbers(array) / array.length : 0

export const pickRandomValue = <T, S extends boolean=true>(
  col: T[],
  strict?: S,
  max_call = MAX_RECURSION_DEPTH_MEDIUM,
): S extends true ? T : T | undefined => {
  const _resultCanBeEmpty = col.some(value => isNull(value))

  const _recurse = (
    col: T[],
    max_recurse: number,
  ) => {
    if (max_recurse <= 0)
      throw new Error(`pickRandomValue:Err - max call stack reach (${ max_call })`)

    if (isNull(col))
      throw new Error(`pickRandomValue:Err - input cannot be empty array!`)

    if (col.length < 2)
      return col[0]

    const randomPick = col[Math.floor((Math.random() * col.length))] // randomPick can be undefined on runtime, so need checking bellow

    // is expecting empty result? yes, return instantly
    // skip recurse when strict is FALSE, return instantly
    if (_resultCanBeEmpty || strict === false)
      return randomPick

    return randomPick ?? _recurse(col, --max_recurse)
  }

  return _recurse(col, max_call) as S extends true ? T : T | undefined
}

export const pickRandomValues = <T, S extends boolean=true>(
  col: T[],
  howMuch = 10,
  strict?: S,
  max_call = MAX_RECURSION_DEPTH_MEDIUM,
): S extends true ? T[] : Array<T | undefined> => {
  const pickedValuesSet = new Set()

  for (let index = 0; index < howMuch; index++)
    pickedValuesSet.add(pickRandomValue<T, S>(col, strict, max_call))

  return [...pickedValuesSet] as S extends true ? T[] : Array<T | undefined>
}

/** @deprecated please use pickRandomValue instead */
export const randomArrayValue = pickRandomValue

export const hasConsecutive = <T>(array: T[], limit = 3, slice = -1) => {
  if (!Array.isArray(array))
    throw new TypeError(`arg1 must be array`)

  if (slice > 0)
    array = array.slice(0, slice)

  let count = 0
  let value = array[0]
  return Boolean(array.some(a => {
    if (value !== a) {
      count = 0
      value = a
    }

    return ++count >= limit
  }))
}
/* ------------------------- END OF ARRAY LIKE UTILS ------------------------ */

/* ------------------------------ OBJECT UTILS ------------------------------ */
export const isPromise = <T>(objectTarget: T | Promise<T>): objectTarget is Promise<T> => (objectTarget && typeof (objectTarget as Promise<T>).then === `function`) || objectTarget instanceof Promise

/**
 * check if the value is really value
 * @param unk
 * @returns
 */
export const isTrulyValue = (unk: unknown): unk is boolean | string | number | Date =>
  typeof unk === `boolean` || typeof unk === `number` || typeof unk === `string` || typeof unk === `bigint` || unk instanceof Date || unk instanceof Buffer
/**
 * isObject. all object type except null is true, (Object | Array)
 * @param obj
 * @returns
 */

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export const isObject = <T = unknown>(object: T | Record<any, any> | unknown): object is ObjectType<T> => object !== null && typeof object === `object`

/**
 * IsObjectType. only object type is true
 * @param obj
 * @returns
 */
export const isObjectOnlyType = <T = unknown>(object: T | Record<any, any> | unknown): object is ObjectType<T> => !Array.isArray(object) && isObject(object)

/**
 * IsPlainObject. oll Object that share Object.prototype is true. Exclude classes (Null | Object)
 * @param obj
 * @returns
 */
export const isPlainObject = (object: unknown) => {
  const isDefinedObject = (object_: unknown): object_ is Record<string, unknown> =>
    typeof object_ === `object`
    && object_ !== null
    && Object.prototype.toString.call(object_) === `[object Object]`

  if (object === null)
    return true

  if (!isDefinedObject(object))
    return false

  const { constructor } = object
  if (typeof constructor === `undefined`)
    return true

  const { prototype } = constructor
  return isDefinedObject(prototype) && Object.prototype.hasOwnProperty.call(prototype, `isPrototypeOf`)
}

export const toPlainObject = <T>(
  object: T,
  max_stacks = MAX_RECURSION_DEPTH_MEDIUM,
): T | undefined => {
  if (isObject(object)) {
    const plainObject: T | undefined = { ...object }
    for (const key in object) {
      if (Object.prototype.hasOwnProperty.call(object, key)) {
        if (max_stacks > 0) {
          const transformed = toPlainObject(object[key], max_stacks - 1)
          if (!isEmpty(transformed))
            plainObject[key] = transformed
        } else {
          plainObject[key] = object[key]
        }
      }
    }

    return plainObject
  }

  return void 0
}

export const isEqual = (x: unknown, y: unknown) => {
  const shallowEqual = x === y
  if (shallowEqual)
    return true

  const deepEqual = () => {
    if (isObject(x) && isObject(y))
      return toPlainObject(x) === toPlainObject(y)

    return false
  }

  if (deepEqual())
    return true

  return false
}

export const omitKeyProperty = <T extends object>(
  object: T, key: keyof T, maxStacks = 12,
) => {
  if (maxStacks > 0 && (object && typeof object === `object`))
    delete object[key]

  return object
}

export const omitKeysProperty = <T extends AnyObject>(
  object: T, keys: Array<keyof T> | string[], maxStacks = 12,
): T => {
  if (Array.isArray(keys)) {
    for (const propertyKey of Object.values(keys)) {
      if (maxStacks > 0 && (object && typeof object === `object`))
        delete object[propertyKey]
    }

    return object
  }

  throw new Error(`arg2 must be array`)
}

export const omitUndefinedProperty = <T extends Record<string, any>>(object: T, _ = void 0 as any, __ = void 0 as any, maxStacks = 12) => {
  if (maxStacks > 0 && (object && typeof object === `object`)) {
    for (const key of Object.keys(object))
      typeof object[key] === `undefined` ? delete object[key] : void 0
  }

  return object
}

export const toString = <T>(unk: T): string | undefined => isEmpty(unk)
  ? void 0
  : (isObjectID(unk)
    ? ObjectIDtoString(unk)
    : `${ unk }`)

export const toStringStrict = <T, R>(unk: T, throwHandler: boolean | ((unk: T) => R) = true): string | R => isEmpty(unk)
  ? (() => {
    if (throwHandler && typeof throwHandler === `function`)
      return throwHandler(unk)
    throw new Error(`${ unk } is empty`)
  })()
  : (isObjectID(unk)
    ? ObjectIDtoStringStrict(unk)
    : `${ unk }`)

/**
 * this function removes empty properties recursively
 * @param object
 * @param _
 * @param __
 * @param maxStacks
 * @returns
 */
export const omitEmptyProperty = <T extends Record<string, unknown>>(object: T, _ = void 0 as any, __ = void 0 as any, maxStacks = 12) => {
  if (maxStacks > 0 && (object && typeof object === `object`)) {
    for (const key of Object.keys(object))
      isEmpty(typeof object[key]) ? delete object[key] : void 0
  }

  return object
}

/**
 *
 * If arg 2 is true, it will throw an error if the env variable is not defined
 * @param varValue T
 * @param strict boolean
 * @returns NonNullable<T>
 */

export const isNull = (value: unknown): value is null | undefined =>
  value === null || value === undefined

export const nonNullValue = <E, S extends boolean = true>(
  variableValue: E,
  strict?: S,
  variableName = ``,
) => {
  if (isNull(variableValue) && strict) {
    throw new Error(
      `nonNullValue - varValue ${ variableName } is not defined!`,
    )
  }

  return variableValue as S extends true ? NonNullable<E> : NonNullable<E> | undefined
}

/**
 * Convert anything to number if not NaN
 * @param unk
 * @returns
 */
export const toNumberOrUndefined = (unk: unknown) => !isEmpty(unk) && !Number.isNaN(Number(unk)) ? Number(unk) : void 0

/**
 * Convert object array like to array, ex: {0: {},1:{}} => [{},{}]
 * @param object
 * @returns
 */
export const objectToArray = <T>(object: T) => isObject(object) && Object.keys(object).every(k => typeof toNumberOrUndefined(k) === `number`)
  ? Object.values(object) as T extends Record<any, (infer V)> ? V : T
  : object

/* --------------------------- END OF OBJECT UTILS -------------------------- */

export const randNumber = ({ max, min }: { min: number; max: number }) => Math.floor(Math.random() * (max - min + 1) + min)
